import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import supabase from '../lib/supabaseClient';

// Hard gate: an authenticated founder whose tenant has not finished onboarding
// cannot reach any protected route. They are bounced to /onboarding (which lives
// OUTSIDE this guard in App.js, so there is no redirect loop).
//
// FAIL-OPEN BY DESIGN. We only block on positive confirmation that
// onboarding_complete is literally false. On any error, timeout, or unknown
// status we let the user through. This is deliberate: a false negative shows a
// founder the platform a beat early; a false positive locks a paying tenant out
// of their own account (the 2026-05-29 HealthPlus CEO lockout). Open is the safe
// failure.
//
// We cache ONLY the positive result ('true'), keyed by tenant id so a logout +
// different login in the same tab can never inherit the prior tenant's status.
// Completed tenants (the 99% case) then resolve synchronously on every subsequent
// navigation with no loader flash and no repeat query. We never cache 'false' so
// the moment onboarding finishes, the next guarded route re-checks the DB and lets
// them in without a stale redirect loop.
const cacheKeyFor = (tenantId) => `onboarding_complete_${tenantId}`;

export default function OnboardingGuard({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  const tenantId = user?.tenant_id;
  const cacheKey = tenantId ? cacheKeyFor(tenantId) : null;

  // Seed from cache synchronously: 'true' => complete, otherwise unknown.
  const [complete, setComplete] = useState(() =>
    cacheKey && sessionStorage.getItem(cacheKey) === 'true' ? true : undefined
  );

  // Invited team members never run onboarding themselves — never gate them.
  const isInvited = !!(
    user?.user_metadata?.invited_signup || user?.user_metadata?.invitation_id
  );

  useEffect(() => {
    let active = true;
    // Resolve status only when we have a tenant, aren't invited, and don't
    // already trust a cached completion.
    if (!tenantId || isInvited) return;
    if (sessionStorage.getItem(cacheKeyFor(tenantId)) === 'true') {
      setComplete(true);
      return;
    }

    (async () => {
      try {
        const { data, error } = await supabase
          .from('tenants')
          .select('onboarding_complete')
          .eq('id', tenantId)
          .single();
        if (!active) return;
        // Fail open: anything other than an explicit false counts as complete.
        const isComplete = error ? true : data?.onboarding_complete !== false;
        if (isComplete) sessionStorage.setItem(cacheKeyFor(tenantId), 'true');
        setComplete(isComplete);
      } catch {
        if (active) setComplete(true);
      }
    })();

    return () => {
      active = false;
    };
  }, [tenantId, isInvited]);

  if (loading) return <div>Loading...</div>;
  // Not authenticated — defer to ProtectedRoute (unchanged behavior).
  if (!user) return children;
  // Invited users and confirmed-complete tenants pass straight through.
  if (isInvited || complete === true) return children;
  // Status still resolving: hold rather than flash the platform.
  if (complete === undefined) return <div>Loading...</div>;
  // Positive confirmation onboarding is incomplete — gate to onboarding.
  if (location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }
  return children;
}
